import { useDurationHelpers } from '@/features/common/composables/useDurationHelpers';
import { type TransitInfoDto, TransportSubmode, TripMode } from '@/types/webapi';
import { useI18n } from 'vue-i18n';

export function useTripModeHelper() {
    const { t } = useI18n();
    const { humanizeDuration } = useDurationHelpers();

    function getTripStartText(tripMode?: TripMode, transitInfo?: TransitInfoDto) {
        switch (tripMode) {
            case TripMode.Bus:
                return t('trips.resources.tripLegLabels.getOnBus', 'Take the bus');
            case TripMode.Rail:
                return t('trips.resources.tripLegLabels.getOnTrain', 'Take the train');
            case TripMode.Water: {
                const { transportSubmode } = transitInfo || {};
                switch (transportSubmode) {
                    case TransportSubmode.InternationalCarFerry:
                    case TransportSubmode.LocalCarFerry:
                    case TransportSubmode.NationalCarFerry:
                    case TransportSubmode.RegionalCarFerry:
                        return t('trips.resources.tripLegLabels.getOnCarFerry', 'Take the car ferry');
                    default:
                        return t('trips.resources.tripLegLabels.getOnBoat', 'Take the speedboat');
                }
            }
            default:
                return t('trips.resources.tripLegLabels.getOnVehicle', 'Get on');
        }
    }

    function getTripMiddleText(tripMode?: TripMode) {
        switch (tripMode) {
            case TripMode.Foot:
                return t('trips.resources.tripLegLabels.walkTo', 'Walk to');
            case TripMode.Bicycle:
                return t('trips.resources.tripLegLabels.bicycleTo', 'Bicycle to');
            default:
                return t('trips.resources.tripLegLabels.getTo', 'Get to');
        }
    }

    function getTripEndText(tripMode?: TripMode, transitInfo?: TransitInfoDto) {
        switch (tripMode) {
            case TripMode.Bus:
                return t('trips.resources.tripLegLabels.getOffBusAt', 'Get off the bus at');
            case TripMode.Rail:
                return t('trips.resources.tripLegLabels.getOffTrainAt', 'Get off the train at');
            case TripMode.Water: {
                const { transportSubmode } = transitInfo || {};
                switch (transportSubmode) {
                    case TransportSubmode.InternationalCarFerry:
                    case TransportSubmode.LocalCarFerry:
                    case TransportSubmode.NationalCarFerry:
                    case TransportSubmode.RegionalCarFerry:
                        return t('trips.resources.tripLegLabels.getOffCarFerryAt', 'Get off the car ferry at');
                    default:
                        return t('trips.resources.tripLegLabels.getOffBoatAt', 'Get off the speedboat at');
                }
            }
            default:
                return t('trips.resources.tripLegLabels.getOffVehicleAt', 'Get off at');
        }
    }

    function getNonTransitDurationText(tripMode: TripMode, duration: number) {
        switch (tripMode) {
            case TripMode.Foot:
                return t('trips.resources.tripLegLabels.walkForDuration', { duration: humanizeDuration(duration) }, '{duration} walk');
            default:
                return humanizeDuration(duration);
        }
    }

    return {
        getTripStartText,
        getTripMiddleText,
        getTripEndText,
        getNonTransitDurationText
    };
}
